// 資訊頁的「跨遊戲共用」段落 — 逐節點對照 Unity `SlotFramework/Prefab/UI/Base/InfoCommonView.prefab`
// 與 `Script/UI/Panel/{InfoPanel,InfoCommonView}.cs`。
//
// 結構：遊戲玩法標題 → 13 個操作說明列（Layout_*）→ 遊戲中斷 → 一般條款。
// 每個 Layout_* 是巢狀 VerticalLayoutGroup（spacing 5），內含「標題＋數行說明」，
// 標題與說明的 TMP 都以 `m_margin.x = 150` 左內縮讓出左緣圖示欄，圖示是標題的子節點（絕對定位、不佔高度）。
//
// 動態參數（對照 InfoPanel.cs）：
//   `_FG`        `{0}` = 幣別**代碼**（Unity `currency.Code`，如 PHP，不是符號）
//   `_Main_Way`  `{0}` = ClientOptions.RTP[0]（主遊戲 RTP）
//   `BetRange`   `{0}`/`{1}` = BetRange 首末項
//
// 依 Unity 條件顯示／隱藏的節點：
//   `_NO_FG`／`_Main_Line`  ─ 本作有免費遊戲且為 ways 制，故永遠用 `_FG`／`_Main_Way`（另一半永遠隱藏）
//   `Layout_FreeSpin`／`_ExBet` ─ prefab 內即為 OFF，需後端提供各 BuyType 的 RTP 才會開，本移植不做
//   `Layout_Home`／`Layout_History` ─ Unity 依平台 HomeUrl／RecordUrl 是否存在決定；本專案無平台環境，
//                                     與選單的 Home/Log 一致保留顯示（那兩個鈕本身也還是佔位）
//   `_Main_Way` ─ Unity 依 ClientOptions.ShowRTP 決定；後端沒給就沿用示意值並標明。

import { t } from '../../../../framework/i18n/i18n'
import { getUiTexture } from '../../../assets/UiTextures'
import { bodyBlock, COMMON_TITLE_GRADIENT, CONTENT_W, iconRowBlock, titleBlock, type InfoBlock, type RowIcon } from './infoBlocks'

/** 圖示欄的標準尺寸（prefab 的 Image sizeDelta，除 Space Bar 與 Close 另有值）。 */
const ICON = 100

/** 後端沒提供 ClientOptions.RTP 時的示意值（與舊版資訊頁一致，並在文案上標明非權威）。 */
export const FALLBACK_RTP = '96.67'

export interface CommonSectionData {
  /** 幣別代碼（Unity 用 `currency.Code`，例如 PHP）。 */
  readonly currencyCode: string
  /** 主遊戲 RTP 字串（來自 ClientOptions.RTP[0]；空字串代表後端未提供）。 */
  readonly rtp: string
  /** 押注區間顯示字串（已格式化）。 */
  readonly minBet: string
  readonly maxBet: string
}

/** 13 個操作說明列，順序即 prefab 的子節點順序。 */
function iconRows(): InfoBlock[] {
  const icon = (key: Parameters<typeof getUiTexture>[0]): RowIcon => ({
    texture: getUiTexture(key),
    width: ICON,
    height: ICON,
  })
  const rows: ReadonlyArray<readonly [RowIcon, string, readonly string[]]> = [
    [icon('spinButton'), 'common.Info_Spin', ['common.Info_Spin_1', 'common.Info_Spin_2']],
    [icon('spinStop'), 'common.Info_Stop', ['common.Info_Stop_1']],
    // Space Bar 的圖示是 84×84 且 aPos (8,−8)（prefab 原值）。
    [
      { texture: getUiTexture('keySpace'), width: 84, height: 84, offsetX: 8, offsetY: 8 },
      'common.Info_SpaceBar',
      ['common.Info_SpaceBar_1'],
    ],
    [icon('iconWallet'), 'common.Info_Wallet', ['common.Info_Wallet_1']],
    [icon('iconBet'), 'common.Info_Bet', ['common.Info_Bet_1']],
    [icon('iconAuto'), 'common.Info_AutoSpin', ['common.Info_AutoSpin_1', 'common.Info_AutoSpin_2']],
    [icon('iconInfo'), 'common.Info_Rules', ['common.Info_Rules_1', 'common.Info_Rules_2']],
    [icon('iconMenu'), 'common.Info_MoreSetting', ['common.Info_MoreSetting_1']],
    [icon('iconHome'), 'common.Info_Home', ['common.Info_Home_1']],
    [icon('iconTurbo'), 'common.Info_Turbo', ['common.Info_Turbo_1']],
    [icon('iconVolume'), 'common.Info_Sound', ['common.Info_Sound_1']],
    [icon('iconLog'), 'common.Info_History', ['common.Info_History_1']],
    // 關閉鈕的圖示是 btn_circle 157×157 縮放 0.6，中央疊 btn_close 84×84。
    [
      {
        texture: getUiTexture('btnCircle'),
        width: 157,
        height: 157,
        scale: 0.6,
        overlay: { texture: getUiTexture('btnClose'), width: 84, height: 84 },
      },
      'common.Info_Close',
      ['common.Info_Close_1'],
    ],
  ]
  return rows.map(([rowIcon, titleKey, descKeys]) =>
    iconRowBlock(
      rowIcon,
      t(titleKey),
      descKeys.map((key) => t(key)),
    ),
  )
}

/** 依序回傳 InfoCommonView 的共用段落。 */
export function commonSections(data: CommonSectionData): InfoBlock[] {
  const title = (key: string): InfoBlock => titleBlock(t(key), COMMON_TITLE_GRADIENT)
  const body = (text: string): InfoBlock => bodyBlock(text, CONTENT_W, 'left')
  const rtpKnown = data.rtp.length > 0

  return [
    title('common.Info_HowToPlay'),
    ...iconRows(),

    title('common.Info_Interrupted'),
    body(t('common.Info_Interrupted_1')),

    title('common.Info_General'),
    body(t('common.Info_General_1_fg', { 0: data.currencyCode })),
    // Unity 這段的文案聲稱「賠付反映目前選擇的投注等級」，但賠付表其實是固定的參考注額值（見 PayTableGrid）；
    // 這是來源文案與實作的既有落差，照搬不改寫。RTP 後端沒給時附註為示意值，避免誤導。
    body(
      rtpKnown
        ? t('common.Info_General_2_WAY', { 0: data.rtp })
        : `${t('common.Info_General_2_WAY', { 0: FALLBACK_RTP })}\n${t('info.rtpFallbackNote')}`,
    ),
    body(t('common.Info_General_5', { 0: data.minBet, 1: data.maxBet })),
    body(t('common.Info_General_6')),
  ]
}
